import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import clsx from 'clsx';
import { beautifyEnum } from 'src/utils/utility';
import IconMerge from 'src/assets/images/icons/merge.svg';
import IconMergeActive from 'src/assets/images/icons/merge-active.svg';

interface SelectionIndicatorProps<T extends { id?: number; code?: string }> {
  selectedItems: T[];
  onClearSelection: () => void;
  onMergeClick?: () => void;
  className?: string;
}

export const SelectionIndicator = <T extends { id?: number; code?: string }>({
  selectedItems,
  onClearSelection,
  onMergeClick,
  className,
}: SelectionIndicatorProps<T>): JSX.Element | null => {
  if (selectedItems.length === 0) return null;

  return (
    <div
      className={clsx(
        'p-16 rounded-12 bg-[#1C2429] flex items-center gap-16 max-w-[720px] w-full fixed bottom-[46px] left-1/2 -translate-x-1/2 z-50 text-white',
        className
      )}
    >
      <button type='button' onClick={onClearSelection} aria-label='Close' className='text-neutral-50'>
        <CloseIcon />
      </button>
      <strong className='text-14 font-normal'>{selectedItems.length} item selected</strong>

      {selectedItems.length === 1 && (
        <>
          <span className='w-1 h-14 bg-[#373E43]' />
          <strong className='text-14 font-medium'>
            No. {selectedItems[0].id} {beautifyEnum(selectedItems[0].code || '')}
          </strong>
        </>
      )}

      {selectedItems.length === 2 && (
        <>
          <span className='w-1 h-14 bg-[#373E43]' />
          <span className='text-14 font-medium flex items-center gap-10'>
            Merge [
            <span className='text-primary max-w-[110px] truncate'>
              No. {selectedItems[0].id} {beautifyEnum(selectedItems[0].code || '')}
            </span>
            <ArrowBackIcon className='text-red-40' />
            <span className='text-red-40 max-w-[110px] truncate'>
              No. {selectedItems[1].id} {beautifyEnum(selectedItems[1].code || '')}
            </span>
            ]
          </span>
        </>
      )}

      <div className='ml-auto'>
        <button
          type='button'
          disabled={selectedItems.length !== 2}
          className={clsx('py-6 px-16 border border-[#475056] rounded-6 flex items-center gap-10', {
            'text-white': selectedItems.length === 2,
            'text-neutral-50': selectedItems.length !== 2,
          })}
          onClick={onMergeClick}
        >
          Merge
          <img src={selectedItems.length === 2 ? IconMergeActive : IconMerge} alt='Merge' />
        </button>
      </div>
    </div>
  );
};
